// @givmo/payments — Shift4.js CDN script-loader hook.
//
// Shift4.js MUST load from Shift4's CDN and is NEVER bundled or self-hosted — Shift4's
// PCI iframes (the SAQ-A mechanism) only work when the script is served from their origin.
//
// HOST (03 §9, kickoff-verified 2026-06-24): standardize on the *documented*
// `https://js.dev.shift4.com/shift4.js` for ALL environments. The `pu_test_`/`pu_live_`
// (publishable `pk_test_`/`pk_live_`) key prefix sets test-vs-live mode — `js.dev.*` is a
// misnomer, not an env gate. NEVER use the root `https://js.shift4.com/shift4.js` (403s).
// An undocumented prod build exists at `js.shift4.com/v1/shift4.js` — do NOT adopt it
// without Shift4 sign-off (routed to the 01-SHIFT4 letter ask-list). Hence a single
// canonical URL, exported so consumers (and the CSP allowlist) stay in lock-step.

/** The single canonical Shift4.js embed host. See file header for the host decision. */
export const SHIFT4_JS_SRC = 'https://js.dev.shift4.com/shift4.js' as const;

import type { Shift4Factory, Shift4Instance } from './shift4-types';

/** Async loader state for the React hook. */
export type Shift4LoadStatus = 'idle' | 'loading' | 'ready' | 'error';

let scriptPromise: Promise<Shift4Factory> | null = null;

/**
 * Resolve the `window.Shift4` factory, injecting the CDN `<script>` once and reusing the
 * same promise on subsequent calls. Idempotent across the whole app: if the script tag is
 * already present (e.g. injected by another consumer or SSR), it is reused rather than
 * re-added. Rejects if the script fails to load or the global never appears.
 *
 * Safe to call during SSR-guarded effects only — it touches `document`/`window`.
 */
export function loadShift4(src: string = SHIFT4_JS_SRC): Promise<Shift4Factory> {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return Promise.reject(new Error('Shift4.js can only be loaded in a browser environment.'));
  }
  if (window.Shift4) {
    return Promise.resolve(window.Shift4);
  }
  if (scriptPromise) {
    return scriptPromise;
  }

  scriptPromise = new Promise<Shift4Factory>((resolve, reject) => {
    // The <script> THIS call injects, or null when we reuse a pre-existing/SSR tag. On a failed
    // attempt we remove OUR OWN injected tag before clearing the cache, so a retry injects a fresh
    // tag instead of re-attaching to an already-settled one. A settled tag never re-fires
    // `load`/`error`, so without this the retry's promise would hang forever on the payment path.
    // We never remove a tag we did not create — an external/SSR tag is its owner's to manage.
    let injected: HTMLScriptElement | null = null;

    const fail = (message: string) => {
      if (injected) injected.remove();
      scriptPromise = null;
      reject(new Error(message));
    };

    const onReady = () => {
      if (window.Shift4) {
        resolve(window.Shift4);
      } else {
        fail('Shift4.js loaded but window.Shift4 was not defined.');
      }
    };
    const onError = () => {
      fail(`Failed to load Shift4.js from ${src}.`);
    };

    const existing = document.querySelector<HTMLScriptElement>(`script[src="${src}"]`);
    if (existing) {
      // A tag already exists — it may or may not have finished loading. If the global were
      // present we'd have returned above; otherwise wait for its load/error.
      existing.addEventListener('load', onReady, { once: true });
      existing.addEventListener('error', onError, { once: true });
      return;
    }

    const script = document.createElement('script');
    injected = script;
    script.src = src;
    script.async = true;
    // No `crossorigin`/SRI: Shift4 ships an unversioned, frequently-updated bundle, so a
    // pinned integrity hash would break on their next push. Integrity is enforced at the
    // CSP layer (script-src allowlist of the Shift4 host) — see `csp.ts`.
    script.addEventListener('load', onReady, { once: true });
    script.addEventListener('error', onError, { once: true });
    document.head.appendChild(script);
  });

  return scriptPromise;
}

/**
 * Reset the cached loader promise. Test-only seam — production code should never need to
 * re-load Shift4.js. Exposed so unit tests can isolate the module-level singleton.
 */
export function resetShift4LoaderForTests(): void {
  scriptPromise = null;
}

/** Create a `Shift4` instance for a publishable key once the factory is loaded. */
export async function createShift4(
  publishableKey: string,
  src: string = SHIFT4_JS_SRC,
): Promise<Shift4Instance> {
  const factory = await loadShift4(src);
  return factory(publishableKey);
}
